import { GoalCategoryFullFragment, GoalTargetForTableFragment, useGoalCategoriesQuery } from "apps/web/src/types";
import React, { ReactNode, createContext, useContext, useState } from "react";
import { PaginationHooks, useOffsetPagination } from "../../utils/pagination";

type GoalConfigurationTemplateContextValue = {
    paginationHooks: PaginationHooks;
    loading: boolean;
    category: GoalCategoryFullFragment;
    categoryOptions: GoalCategoryFullFragment[];
    setCategory: (category: GoalCategoryFullFragment) => void;
    target: GoalTargetForTableFragment;
    targetOptions: GoalTargetForTableFragment[];
    setTarget: (target: GoalTargetForTableFragment) => void;
    createdByMe: boolean | null;
    setCreatedByMe: (value: boolean | null) => void;
};

const categoryPlaceholder = { id: "none", defaultEmoji: "", name: "Select Type" };
const criteriaPlaceholder = { id: "none", unit: "Select Criteria", value: 0 };
const targetOptions: GoalTargetForTableFragment[] = [
    criteriaPlaceholder,
    { id: "progress", unit: "Update Progress", value: 1 },
    { id: "complete", unit: "Mark as Complete", value: 0 }
];

const defaultState: GoalConfigurationTemplateContextValue = {
    paginationHooks: {} as PaginationHooks,
    loading: true,
    category: categoryPlaceholder,
    setCategory: () => { },
    target: criteriaPlaceholder,
    setTarget: () => { },
    createdByMe: null,
    setCreatedByMe: () => { },
    categoryOptions: [],
    targetOptions: []
};

const GoalConfigurationTemplatePageContext = createContext<GoalConfigurationTemplateContextValue>(defaultState);

const ROWS_PER_PAGE = 15;

export const GoalConfigurationTemplateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [category, setCategory] = useState<GoalCategoryFullFragment>(categoryPlaceholder);
    const [target, setTarget] = useState<GoalTargetForTableFragment>(criteriaPlaceholder);
    const [createdByMe, setCreatedByMe] = useState<boolean | null>(null);
    const { data: categoriesData } = useGoalCategoriesQuery();
    const categories = categoriesData?.goalCategories ?? [];
    const paginationHooks = useOffsetPagination(ROWS_PER_PAGE);

    return (
        <GoalConfigurationTemplatePageContext.Provider
            value={{
                paginationHooks,
                loading: false,
                category,
                setCategory,
                target,
                setTarget,
                createdByMe,
                setCreatedByMe,
                categoryOptions: [categoryPlaceholder, ...categories],
                targetOptions
            }}
        >
            {children}
        </GoalConfigurationTemplatePageContext.Provider>
    );
};

export const useGoalConfigurationTemplatePageContext = () => {
    return useContext(GoalConfigurationTemplatePageContext);
};
